import { createSlice, isPending, isRejected, PayloadAction } from '@reduxjs/toolkit';
import { showApiErrors } from '@/utils';
import { OrdersState, ORDERS_SLICE_NAME, initialState } from './models';
import {
  getOrdersList,
  getOrderDetails,
  addOrderComment,
  updateOrderDelivery,
  updateOrderPayment,
  updateOrderPaymentChannel,
  updateOrderProducts,
  changeOrderStatus,
  changeOrderRegion,
  getOrderProductsList,
  getNovaPoshtaDepartments,
  updateOrderClient,
  getManagersList,
  changeOrderManager,
  getNewOrdersCount,
  recordAdditionalPayment,
  recordRefund,
} from './actionCreators';
import { TGetOrdersQuery } from '../helpers';

export const slice = createSlice({
  name: ORDERS_SLICE_NAME,
  initialState,
  reducers: {
    resetOrdersListState(state: OrdersState) {
      return {
        ...initialState,
        newCount: state.newCount,
        isNewCountLoading: state.isNewCountLoading,
      };
    },
    resetOrderInfoState(state: OrdersState) {
      state.order = initialState.order;
      state.orderProductsList = initialState.orderProductsList;
    },
    changeQuery: (state: OrdersState, action: PayloadAction<TGetOrdersQuery>) => {
      state.query = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrdersList.fulfilled, (state: OrdersState, action) => {
        state.ordersList = action.payload;
        state.isLoading = false;
      })
      .addCase(getOrderProductsList.fulfilled, (state: OrdersState, action) => {
        state.orderProductsList = action.payload;
        state.isLoadingOrderProducts = false;
      })
      .addCase(getOrderDetails.fulfilled, (state: OrdersState, action) => {
        state.order = action.payload.data;
        state.isLoading = false;
        state.isUpdatingProducts = false;
      })
      .addCase(updateOrderClient.fulfilled, (state: OrdersState, action) => {
        if (!state.order) return;

        state.order = { ...state.order, ...action.payload };
        state.isLoading = false;
      })
      .addCase(addOrderComment.fulfilled, (state: OrdersState, action) => {
        if (state.order) {
          state.order = {
            ...state.order,
            sub_orders: state.order.sub_orders.map((el) => {
              if (el.id === action.payload.subOrder_id) {
                return { ...el, comments: [...el.comments, action.payload.data] };
              }
              return el;
            }),
          };
        }

        state.isLoading = false;
      })
      .addCase(updateOrderDelivery.fulfilled, (state: OrdersState, action) => {
        if (state.order?.delivery) {
          state.order = {
            ...state.order,
            delivery: {
              ...state.order.delivery,
              delivery: action.payload.delivery_type,
            },
          };
        }

        state.isLoading = false;
      })
      .addCase(changeOrderStatus.fulfilled, (state: OrdersState, action) => {
        if (state.order) {
          state.order = {
            ...state.order,
            status: action.payload.status,
          };
        }

        state.isLoading = false;
      })
      .addCase(changeOrderRegion.fulfilled, (state: OrdersState) => {
        state.isLoading = false;
      })
      .addCase(updateOrderPayment.fulfilled, (state: OrdersState, action) => {
        if (state.order) {
          state.order = {
            ...state.order,
            payment_status: action.payload.payment_status,
          };
        }

        state.isLoading = false;
      })
      .addCase(updateOrderPaymentChannel.fulfilled, (state: OrdersState, action) => {
        if (state.order) {
          state.order = {
            ...state.order,
            payment_channel: action.payload.payment_channel,
          };
        }

        state.isLoading = false;
      })
      .addCase(getNovaPoshtaDepartments.fulfilled, (state: OrdersState, action) => {
        state.novaPoshtaDepartments = action.payload.data;
        state.isLoadingNovaPoshta = false;
      })
      .addCase(getManagersList.fulfilled, (state: OrdersState, action) => {
        state.managers = action.payload.data;
      })
      .addCase(changeOrderManager.fulfilled, (state: OrdersState, action) => {
        if (state.order) {
          state.order = { ...state.order, manager: action.payload.data.manager };
        }
        state.isLoading = false;
      })
      .addCase(getNewOrdersCount.pending, (state: OrdersState) => {
        state.isNewCountLoading = true;
      })
      .addCase(getNewOrdersCount.fulfilled, (state: OrdersState, action) => {
        state.isNewCountLoading = false;
        state.newCount = action.payload.data.count;
      })
      .addCase(getNewOrdersCount.rejected, (state: OrdersState) => {
        state.isNewCountLoading = false;
      })
      .addCase(getOrderProductsList.pending, (state: OrdersState) => {
        state.error = null;
        state.isLoadingOrderProducts = true;
      })
      .addCase(getNovaPoshtaDepartments.pending, (state: OrdersState) => {
        state.error = null;
        state.isLoadingNovaPoshta = true;
      })
      .addCase(getOrderProductsList.rejected, (state: OrdersState, action) => {
        state.error = action.error;
        state.isLoadingOrderProducts = false;
      })
      .addCase(getNovaPoshtaDepartments.rejected, (state: OrdersState, action) => {
        state.error = action.error;
        state.isLoadingNovaPoshta = false;
      })
      .addCase(updateOrderProducts.pending, (state: OrdersState) => {
        state.isUpdatingProducts = true;
        state.error = null;
      })
      .addCase(updateOrderProducts.fulfilled, (state: OrdersState) => {
        state.error = null;
      })
      .addCase(updateOrderProducts.rejected, (state: OrdersState, action) => {
        state.isUpdatingProducts = false;
        state.error = action.error;
      })
      .addMatcher(isPending(getOrderDetails), (state: OrdersState) => {
        if (!state.isUpdatingProducts) {
          state.isLoading = true;
          state.error = null;
        }
      })
      .addMatcher(
        isPending(
          getOrdersList,
          addOrderComment,
          updateOrderDelivery,
          updateOrderPayment,
          updateOrderPaymentChannel,
          changeOrderStatus,
          changeOrderRegion,
          updateOrderClient,
          changeOrderManager,
          recordAdditionalPayment,
          recordRefund,
        ),
        (state: OrdersState) => {
          state.isLoading = true;
          state.error = null;
        },
      )
      .addMatcher(
        isRejected(
          getOrdersList,
          getOrderDetails,
          addOrderComment,
          updateOrderDelivery,
          updateOrderPayment,
          updateOrderPaymentChannel,
          changeOrderStatus,
          changeOrderRegion,
          updateOrderClient,
          changeOrderManager,
          recordAdditionalPayment,
          recordRefund,
        ),
        (state: OrdersState, action) => {
          const { error } = action;
          state.isLoading = false;
          state.isUpdatingProducts = false;
          state.error = error;

          showApiErrors(error);
        },
      );
  },
});

export const { resetOrdersListState, resetOrderInfoState, changeQuery } = slice.actions;

export default slice.reducer;
